import React, { useState, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [loginKey, setLoginKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Forcer le re-rendu du bouton Google si la bibliothèque GSI n'est pas prête
  useEffect(() => {
    // Attendre que le script Google Identity Services soit chargé
    const checkGsiReady = () => {
      if (window.google?.accounts?.id) {
        return;
      }
      // Retry après un court délai si pas encore prêt
      const timeout = setTimeout(() => {
        setLoginKey(prev => prev + 1);
      }, 500);
      return () => clearTimeout(timeout);
    };
    
    checkGsiReady();
  }, []);

  const handleGoogleSuccess = useCallback(async (response) => {
    if (!response.credential) {
      console.error("Google credential is missing or invalid.");
      setErrorMessage("La connexion a échoué. Veuillez réessayer.");
      setIsLoading(false);
      return;
    }
  
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/google/callback`, {
        token: response.credential,
      });
  
      const userData = {
        ...res.data.user,
        access_token: res.data.access_token,
      };
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      navigate("/sessions");
    } catch (error) {
      console.error("Error during API call:", error.response?.data || error.message);
      setErrorMessage("Une erreur est survenue lors de la connexion. Veuillez réessayer.");
      setIsLoading(false);
    }
  }, [navigate, setUser]);
  
  const handleGoogleFailure = useCallback((error) => {
    // COOP peut causer des échecs intermittents, on réinitialise le bouton
    setErrorMessage("La connexion a échoué. Veuillez réessayer.");
    // Forcer le re-rendu du bouton Google après un échec
    setTimeout(() => {
      setLoginKey(prev => prev + 1);
      setErrorMessage('');
    }, 1000);
  }, []);

  return (
    <div style={styles.container}>
      {/* Modern Navigation Bar */}
      <nav style={navStyle}>
        <div style={navContainerStyle}>
          <span style={navLogoStyle} onClick={() => navigate("/")} role="button">
            Dansy
          </span>
          
          <div style={navButtonsStyle}>
            <button onClick={() => navigate("/")} style={navBackButton}>
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Login Content */}
      <div style={styles.loginCard}>
        <div style={styles.iconContainer}>
          <div style={styles.icon}>🎭</div>
        </div>
        
        <h1 style={styles.title}>Welcome Back!</h1>
        <p style={styles.subtitle}>
          Sign in to continue creating amazing choreographies
        </p>
        
        <div style={styles.buttonContainer}>
          {isLoading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>Connexion en cours...</p>
            </div>
          ) : (
            <GoogleOAuthProvider
              clientId="538374964776-musvtqu88e56gbcqnp43oo8l8gmp1b4l.apps.googleusercontent.com"
            >
              <GoogleLogin
                key={loginKey}
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleFailure}
                text="signin_with"
                shape="pill"
                locale="en"
                width="300"
                useOneTap={false}
                auto_select={false}
              />
            </GoogleOAuthProvider>
          )}
          {errorMessage && (
            <p style={styles.errorMessage}>{errorMessage}</p>
          )}
        </div>

        <p style={styles.terms}>
          By signing in, you agree to our{' '}
          <a href="/terms" style={styles.link}>Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" style={styles.link}>Privacy Policy</a>
        </p>
      </div>

      {/* Decorative elements */}
      <div style={decorativeCircle1}></div>
      <div style={decorativeCircle2}></div>
    </div>
  );
};

const navStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
};

const navContainerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '20px 40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const navLogoStyle = {
  fontSize: '1.5rem',
  fontFamily: "'Shirkhand', cursive",
  color: '#ff477e',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
};

const navButtonsStyle = {
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
};

const navBackButton = {
  padding: '10px 24px',
  fontSize: '0.95rem',
  backgroundColor: 'transparent',
  color: '#333',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  transition: 'background-color 0.2s ease',
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fff5f7 0%, #ffe8f0 100%)',
    position: 'relative',
    overflow: 'hidden',
    padding: '40px 20px',
  },
  loginCard: {
    backgroundColor: 'white',
    borderRadius: '24px',
    padding: '60px 50px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    maxWidth: '480px',
    width: '100%',
    textAlign: 'center',
    zIndex: 2,
    position: 'relative',
  },
  iconContainer: {
    marginBottom: '24px',
  },
  icon: {
    fontSize: '4rem',
    display: 'inline-block',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#333',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#666',
    marginBottom: '40px',
    lineHeight: 1.6,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'center',
    marginBottom: '32px',
  },
  terms: {
    fontSize: '0.85rem',
    color: '#999',
    lineHeight: 1.5,
  },
  link: {
    color: '#ff477e',
    textDecoration: 'none',
    fontWeight: 500,
  },
  errorMessage: {
    color: '#e53935',
    fontSize: '0.9rem',
    marginTop: '12px',
    fontWeight: 500,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #ff477e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#666',
    fontSize: '0.95rem',
  },
};

const decorativeCircle1 = {
  position: 'absolute',
  width: '400px',
  height: '400px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,71,126,0.15) 0%, rgba(255,71,126,0) 70%)',
  top: '-200px',
  right: '-200px',
  zIndex: 1,
};

const decorativeCircle2 = {
  position: 'absolute',
  width: '300px',
  height: '300px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,107,157,0.15) 0%, rgba(255,107,157,0) 70%)',
  bottom: '-150px',
  left: '-150px',
  zIndex: 1,
};

export default Login;
